import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CarFront, Camera, MapPin, Loader2, CheckCircle2, Circle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/portal/accidents`;
const err = (e) => e?.response?.data?.detail || "حدث خطأ";

const NewReport = ({ onDone }) => {
  const ref = useRef(null);
  const [f, setF] = useState({ accident_date: new Date().toISOString().slice(0, 10), description: "", address: "" });
  const [loc, setLoc] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const getLoc = () => navigator.geolocation?.getCurrentPosition((p) => { setLoc({ lat: p.coords.latitude, lng: p.coords.longitude }); toast.success("تم تحديد موقعك"); }, () => toast.error("فعّل صلاحية الموقع"));
  const send = async () => {
    setBusy(true);
    try {
      const a = (await axios.post(API, { ...f, ...(loc || {}) })).data;
      for (const file of files) { const fd = new FormData(); fd.append("file", file); await axios.post(`${API}/${a.id}/photos`, fd).catch(() => toast.error(`تعذّر رفع ${file.name}`)); }
      toast.success("تم إرسال بلاغ الحادث للإدارة — سلامتك أهم"); onDone();
    } catch (e) { toast.error(err(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3 rounded-2xl border border-rose-100 bg-rose-50/40 p-4" data-testid="pacc-new">
      <p className="font-bold text-rose-700">بلاغ حادث جديد</p>
      <Input type="date" value={f.accident_date} onChange={(e) => setF({ ...f, accident_date: e.target.value })} data-testid="pacc-date" />
      <textarea className="min-h-[80px] w-full rounded-md border border-slate-200 p-2 text-sm" placeholder="وصف ما حصل" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="pacc-desc" />
      <Input placeholder="مكان الحادث (اختياري)" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="pacc-address" />
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={getLoc} data-testid="pacc-loc"><MapPin className="h-4 w-4" /> {loc ? "تم تحديد الموقع ✓" : "إرسال موقعي"}</Button>
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => ref.current?.click()} data-testid="pacc-photos"><Camera className="h-4 w-4" /> صور ({files.length})</Button>
        <input ref={ref} type="file" hidden multiple accept="image/*,application/pdf" onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])} />
      </div>
      <Button onClick={send} disabled={busy || !f.description.trim()} className="w-full bg-rose-600 hover:bg-rose-700" data-testid="pacc-send">{busy && <Loader2 className="h-4 w-4 animate-spin" />} إرسال البلاغ</Button>
    </div>
  );
};

export const PortalAccidents = () => {
  const [d, setD] = useState({ items: [], stages: [] });
  const [adding, setAdding] = useState(false);
  const load = () => axios.get(API).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="portal-accidents">
      <div className="flex items-center gap-2"><CarFront className="h-5 w-5 text-rose-600" /><p className="font-heading font-bold">حوادثي</p>
        {!adding && <Button size="sm" className="ms-auto bg-rose-600 hover:bg-rose-700" onClick={() => setAdding(true)} data-testid="pacc-new-btn">بلاغ حادث</Button>}</div>
      {adding && <NewReport onDone={() => { setAdding(false); load(); }} />}
      {!d.items.length && !adding && <p className="py-8 text-center text-sm text-slate-400" data-testid="pacc-empty">لا توجد حوادث مسجّلة — قيادة آمنة 💚</p>}
      {d.items.map((a) => (
        <div key={a.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`pacc-item-${a.id}`}>
          <div className="flex items-center justify-between"><p className="font-bold">{a.number}</p><span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-700">{a.current_label}</span></div>
          <p className="text-xs text-slate-500">{a.accident_date} · {a.vehicle_label || "—"}</p>
          {a.description && <p className="mt-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-700">{a.description}</p>}
          {!!(a.stages.report?.files || []).length && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`pacc-files-${a.id}`}>
              {a.stages.report.files.map((f) => (/pdf/i.test(f.content_type || "") ? <a key={f.id} href={`${API}/${a.id}/files/${f.id}`} target="_blank" rel="noreferrer" className="rounded bg-slate-100 px-2 py-1 text-[11px] text-indigo-700">{f.name}</a>
                : <a key={f.id} href={`${API}/${a.id}/files/${f.id}`} target="_blank" rel="noreferrer"><img src={`${API}/${a.id}/files/${f.id}`} alt="" className="h-14 w-14 rounded-lg border object-cover" /></a>))}
            </div>
          )}
          <div className="mt-3 space-y-1.5">
            {d.stages.map((s) => { const st = a.stages[s.key] || {}; return (
              <div key={s.key} className="flex items-center gap-2 text-xs">{st.done_at ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-slate-300" />}<span className={st.done_at ? "font-semibold" : "text-slate-400"}>{s.label}</span>{st.done_at && <span className="text-slate-400">{st.done_at}</span>}</div>
            ); })}
          </div>
          {a.fault_pct != null && <p className="mt-2 text-xs">نسبة الخطأ: <b>{a.fault_pct}%</b></p>}
          {a.liability && <p className="mt-1 rounded-lg bg-rose-50 p-2 text-xs text-rose-700" data-testid={`pacc-liab-${a.id}`}>مبلغ محمّل عليك: {a.liability.amount} ر.س · يُخصم {a.liability.installment} ر.س شهرياً</p>}
        </div>
      ))}
    </div>
  );
};
